'use client';

import { useEffect, useMemo, useState } from 'react';
import { Check, CheckCircle2, FileText, Loader2, Search } from 'lucide-react';
import { toast } from '@/lib/toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { StatusBadge } from '@/components/ui/status-badge';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/utils';
import { formatFileSize } from '@/lib/utils/file-utils';
import { searchMigrationFilesAction } from '@/lib/services/smokeball-migration/actions';
import type { SmokeballFile } from '@/lib/services/smokeball-migration/types';
import {
  attachMatterDocument,
  type PmsImportFileType
} from '@/lib/services/redemption/pms-import';

/**
 * Redemption "Attach from Smokeball" (LF-5952) — single-select picker for
 * attaching one PMS document to a Prepare-sheet requirement. Lists the matter's
 * PMS documents (reusing the document-search endpoint) and, on attach, calls
 * the generic import endpoint with the row's `target`. Only mounted when the
 * matter is PMS-connected (see the gate in `prepare-requirements-sheet.tsx`).
 */
export interface PmsImportTarget {
  /** Human label for the dialog copy, e.g. "the Grant of Representation", "Helen Miller's ID". */
  label: string;
  fileType: PmsImportFileType;
  contactId?: number;
  institutionId?: number;
  institutionFormId?: number;
  signatoryContactId?: number;
  inventoryItemId?: number;
  attachmentId?: number;
}

export function AttachFromPmsDialog({
  open,
  onOpenChange,
  matterId,
  providerLabel,
  target,
  onAttached
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: number;
  providerLabel: string;
  target: PmsImportTarget;
  /**
   * Fired after the import lands, with the new Vale `fileId` **and the PMS
   * document it came from**. Target-mode callers (redemption) ignore both and
   * just refresh; callback-mode callers (ADOC-7, LF-6245) reference the imported
   * file by this id, exactly like a choose-existing pick.
   *
   * `file` is what lets the receiving surface name the document (LF-6261): the
   * import is brand new, so it is in no cached matter-files list yet and an
   * id-only hand-off can only fall back to `File <id>`.
   */
  onAttached: (fileId: number, file: SmokeballFile) => void | Promise<void>;
}) {
  const [isSearching, setIsSearching] = useState(true);
  const [files, setFiles] = useState<SmokeballFile[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState<SmokeballFile | null>(null);
  const [isAttaching, setIsAttaching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    const fetchFiles = async () => {
      setIsSearching(true);
      setError(null);
      setSelected(null);
      setSearchQuery('');

      const result = await searchMigrationFilesAction(matterId);
      if (result.success && result.data) {
        setFiles(result.data.files ?? []);
      } else {
        setError(result.error ?? 'Failed to load documents');
      }
      setIsSearching(false);
    };

    void fetchFiles();
  }, [open, matterId]);

  const handleAttach = async () => {
    if (!selected) return;
    setIsAttaching(true);
    setError(null);
    try {
      const { fileId } = await attachMatterDocument(matterId, {
        source: 'pms',
        externalId: selected.externalId,
        fileName: selected.fileName,
        fileType: target.fileType,
        ...(target.contactId != null && { contactId: target.contactId }),
        ...(target.institutionId != null && {
          institutionId: target.institutionId
        }),
        ...(target.institutionFormId != null && {
          institutionFormId: target.institutionFormId
        }),
        ...(target.signatoryContactId != null && {
          signatoryContactId: target.signatoryContactId
        }),
        ...(target.inventoryItemId != null && {
          inventoryItemId: target.inventoryItemId
        }),
        ...(target.attachmentId != null && {
          attachmentId: target.attachmentId
        })
      });
      toast.success(`Attached ${selected.fileName} from ${providerLabel}`);
      await onAttached(fileId, selected);
      onOpenChange(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to attach document'
      );
    } finally {
      setIsAttaching(false);
    }
  };

  const filteredFiles = useMemo(
    () =>
      searchQuery
        ? files.filter((f) =>
            f.fileName.toLowerCase().includes(searchQuery.toLowerCase())
          )
        : files,
    [files, searchQuery]
  );

  const noneOnMatter = !isSearching && files.length === 0;
  const noFilterMatch =
    !isSearching && files.length > 0 && filteredFiles.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Attach from {providerLabel}</DialogTitle>
          <DialogDescription>
            Choose a document from {providerLabel} to attach as {target.label}.
          </DialogDescription>
        </DialogHeader>

        {!isSearching && files.length > 0 && (
          <div className="relative">
            <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
            <Input
              placeholder="Filter documents by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        )}

        <div className="min-h-48">
          {isSearching && (
            <div className="rounded-lg border border-dashed p-12 text-center">
              <Loader2 className="text-foreground-subtle mx-auto mb-3 h-8 w-8 animate-spin" />
              <p className="text-muted-foreground text-sm">
                Loading documents…
              </p>
            </div>
          )}

          {(noneOnMatter || noFilterMatch) && (
            <div className="rounded-lg border border-dashed p-12 text-center">
              <FileText className="text-foreground-subtle mx-auto mb-3 h-12 w-12" />
              <p className="text-muted-foreground text-sm">
                {noFilterMatch
                  ? 'No documents match your filter'
                  : 'No documents found on this matter'}
              </p>
            </div>
          )}

          {filteredFiles.length > 0 && (
            <div className="max-h-96 overflow-y-auto rounded-lg border">
              <Table>
                <TableBody>
                  {filteredFiles.map((file) => {
                    const isSelected = selected?.externalId === file.externalId;
                    return (
                      <TableRow
                        key={file.externalId}
                        className={cn(
                          'cursor-pointer',
                          isSelected
                            ? 'bg-info-subtle/50 hover:bg-info-subtle'
                            : 'hover:bg-muted/50'
                        )}
                        onClick={() => setSelected(file)}
                      >
                        <TableCell width={40}>
                          <span
                            className={cn(
                              'flex h-4 w-4 items-center justify-center rounded-full border',
                              isSelected
                                ? 'border-info bg-info text-info-foreground'
                                : 'border-border-strong'
                            )}
                          >
                            {isSelected && <Check className="h-3 w-3" />}
                          </span>
                        </TableCell>
                        <TableCell className="flex items-center gap-2">
                          <FileText className="text-muted-foreground h-4 w-4" />
                          {/* Same badge, tone and wording the Extract picker uses
                              (`smokeball-search-step.tsx`), and inside the name's
                              span for the same reason: as a flex sibling it detaches
                              from the name it annotates when the name wraps
                              (VALE-89). Unlike Extract this picker still allows the
                              pick — attaching an already-in-Vale document to a
                              requirement is a real workflow, and since VALE-358 it
                              no longer produces a document Vale can push back into
                              the PMS. */}
                          <span
                            className={cn(
                              'min-w-0',
                              isSelected ? 'font-semibold' : 'font-medium'
                            )}
                          >
                            {file.fileName}
                            {file.alreadyMigrated && (
                              <StatusBadge
                                tone="neutral"
                                icon={CheckCircle2}
                                className="ml-2 align-middle whitespace-nowrap"
                              >
                                Already in Vale
                              </StatusBadge>
                            )}
                          </span>
                        </TableCell>
                        <TableCell width={90} className="text-center text-sm">
                          {file.sizeBytes
                            ? formatFileSize(file.sizeBytes)
                            : 'n/a'}
                        </TableCell>
                        <TableCell width={135} className="text-right text-sm">
                          {formatDate(file.dateCreated)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {error && (
            <div className="border-destructive/50 bg-destructive/10 mt-4 rounded-lg border p-3">
              <p className="text-destructive text-sm">{error}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isAttaching}
          >
            Cancel
          </Button>
          <Button onClick={handleAttach} disabled={!selected || isAttaching}>
            {isAttaching ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Attaching…
              </>
            ) : (
              'Attach'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
